// Copy, reference tables and JSON-LD for the free sizing calculators — shared by
// the React pages (src/pages/tools/*) and the prerendered bodies
// (scripts/prerender-body.mjs), so a crawler reads the same words a visitor does.
// Titles and meta descriptions live in scripts/routes.mjs with the other routes.
//
// Copy only restates the published guides (see scripts/sizing-rules.mjs).
import {
  livingRoomRug,
  BED_RUGS,
  diningRug,
  chandelierDiameterIn,
  chandelierOverTableIn,
  chandelierHanging,
  rugLabel,
  RECT_TABLES,
  ROUND_TABLES,
  maxTableFootprint,
  tableLabel,
  artWidthOverFurniture,
  artWidthOnWall,
  ART_GAP_IN,
  ART_CENTER_IN,
  lampshadeSize,
  coffeeTableSize,
  COFFEE_TABLE_GAP_IN,
  COFFEE_TABLE_WALKWAY_IN,
  STOOL_CLASSES,
  STOOL_SPACING_IN,
  stoolsThatFit,
  PATH_CLEARANCE_IN,
  christmasTreeHeightFt,
  christmasTreeWidthIn,
  TREE_HEADROOM_IN,
  TREE_PATH_IN,
  TREE_LIGHTS_PER_FT,
  TREE_ORNAMENTS_PER_FT,
  DOOR_REMOVAL_GAIN_IN,
} from "./sizing-rules.mjs";

const SITE = "https://loftloom.com";

const roundLabel = (ft) => (ft ? `${ft} ft round` : "Larger than 10 ft round");
const rectLabel = (size) => (size ? rugLabel(size) : "Larger than 10×14");

function rugTables() {
  const rooms = [
    [10, 12],
    [11, 14],
    [12, 15],
    [13, 16],
    [12, 18],
    [14, 20],
  ];
  const tables = [
    { label: "36 × 60 in. rectangle", shape: "rectangle", lengthIn: 60, widthIn: 36 },
    { label: "40 × 72 in. rectangle", shape: "rectangle", lengthIn: 72, widthIn: 40 },
    { label: "42 × 84 in. rectangle", shape: "rectangle", lengthIn: 84, widthIn: 42 },
    { label: "48 in. round", shape: "round", diameterIn: 48 },
    { label: "54 in. round", shape: "round", diameterIn: 54 },
    { label: "60 in. round", shape: "round", diameterIn: 60 },
  ];
  return [
    {
      caption: "Living room, front legs on the rug",
      head: ["Room size", "Rug size"],
      rows: rooms.map(([w, l]) => [`${w} × ${l} ft`, rectLabel(livingRoomRug({ roomLengthFt: l, roomWidthFt: w }).size)]),
    },
    {
      caption: "Bedroom",
      head: ["Bed", "Rug size"],
      rows: Object.values(BED_RUGS).map((b) => [b.bed, rugLabel(b.size)]),
    },
    {
      caption: "Dining table (24 in. / 30 in. on every side)",
      head: ["Table", "Minimum", "Roomier"],
      rows: tables.map((t) => {
        const r = diningRug(t);
        const f = t.shape === "round" ? roundLabel : rectLabel;
        return [t.label, f(r.minimum), f(r.roomier)];
      }),
    },
  ];
}

function chandelierTables() {
  const rooms = [
    [10, 10],
    [10, 12],
    [12, 14],
    [14, 16],
    [14, 20],
    [16, 20],
  ];
  const tableWidths = [36, 40, 42, 48, 54, 60];
  const ceilings = [8, 9, 10, 12];
  return [
    {
      caption: "Fixture diameter by room size",
      head: ["Room size", "Diameter"],
      rows: rooms.map(([w, l]) => [`${w} × ${l} ft`, `about ${chandelierDiameterIn({ roomLengthFt: l, roomWidthFt: w })} in.`]),
    },
    {
      caption: "Fixture diameter over a dining table",
      head: ["Table width", "Diameter"],
      rows: tableWidths.map((w) => [`${w} in.`, `${chandelierOverTableIn({ tableWidthIn: w }).join("–")} in.`]),
    },
    {
      caption: "Hanging height by ceiling height",
      head: ["Ceiling", "Over a table (lowest point above the top)", "Walking areas (most the fixture can drop)"],
      rows: ceilings.map((c) => {
        const h = chandelierHanging({ ceilingFt: c });
        return [`${c} ft`, `${h.aboveTable.join("–")} in.`, `${h.walkwayMaxDropIn} in. from the ceiling`];
      }),
    },
  ];
}

function diningTableTables() {
  const seats = (t) => (t.seats[0] === t.seats[1] ? `${t.seats[0]}` : `${t.seats[0]} (${t.seats[1]} at a squeeze)`);
  const rooms = [
    [10, 10],
    [10, 12],
    [11, 14],
    [12, 16],
    [13, 18],
  ];
  return [
    {
      caption: "Rectangular tables",
      head: ["Table", "Seats"],
      rows: RECT_TABLES.map((t) => [tableLabel(t), seats(t)]),
    },
    {
      caption: "Round tables",
      head: ["Table", "Seats"],
      rows: ROUND_TABLES.map((t) => [t.diameter === 42 ? "36–44 in. round" : tableLabel(t), seats(t)]),
    },
    {
      caption: "Largest table by room size (36 in. clear on every side)",
      head: ["Room size", "Largest table footprint"],
      rows: rooms.map(([w, l]) => [`${w} × ${l} ft`, `${maxTableFootprint({ roomLengthFt: l, roomWidthFt: w }).join(" × ")} in.`]),
    },
  ];
}

function wallArtTables() {
  const range = (w) => artWidthOverFurniture(w).join("–");
  return [
    {
      caption: "Art width over furniture (two-thirds to three-quarters)",
      head: ["Furniture", "Furniture width", "Art or group width", "Gap above"],
      rows: [
        ["Sofa", "72 in.", `${range(72)} in.`, `${ART_GAP_IN.sofa.join("–")} in.`],
        ["Sofa", "84 in.", `${range(84)} in.`, `${ART_GAP_IN.sofa.join("–")} in.`],
        ["Sofa", "96 in.", `${range(96)} in.`, `${ART_GAP_IN.sofa.join("–")} in.`],
        ["Queen headboard", "62 in.", `${range(62)} in.`, `${ART_GAP_IN.bed.join("–")} in.`],
        ["King headboard", "78 in.", `${range(78)} in.`, `${ART_GAP_IN.bed.join("–")} in.`],
        ["Console or dresser", "48 in.", `${range(48)} in.`, `${ART_GAP_IN.console.join("–")} in.`],
        ["Console or dresser", "60 in.", `${range(60)} in.`, `${ART_GAP_IN.console.join("–")} in.`],
      ],
    },
    {
      caption: "Art width on an open wall (60–75 percent of the section)",
      head: ["Wall section", "Art or group width", "Center height"],
      rows: [48, 72, 96, 120].map((w) => [`${w} in.`, `${artWidthOnWall(w).join("–")} in.`, `${ART_CENTER_IN.join("–")} in. from the floor`]),
    },
  ];
}

function lampshadeTables() {
  const bases = [
    [5, 15],
    [6, 18],
    [7, 20],
    [8, 22],
    [9, 24],
  ];
  return [
    {
      caption: "Shade size by lamp base",
      head: ["Base width", "Base height (to the socket)", "Shade bottom", "Shade height"],
      rows: bases.map(([w, h]) => {
        const s = lampshadeSize({ baseWidthIn: w, baseHeightIn: h });
        return [`${w} in.`, `${h} in.`, `about ${s.bottomIn} in.`, `about ${s.heightIn} in.`];
      }),
    },
  ];
}

function christmasTreeTables() {
  const ft = (n) => `${n} ft`;
  const ceilings = [7.5, 8, 9, 10, 12];
  return [
    {
      caption: "Tallest tree by ceiling height",
      head: ["Ceiling height", "Tallest tree", "Full tree, across", "Slim tree, across"],
      rows: ceilings.map((c) => {
        const h = christmasTreeHeightFt(c);
        const full = christmasTreeWidthIn(h, "full");
        const slim = christmasTreeWidthIn(h, "slim");
        return [ft(c), ft(h), `about ${full[0]}–${full[1]} in.`, `about ${slim[0]} in.`];
      }),
    },
  ];
}

function coffeeTableTables() {
  return [
    {
      caption: "Sofa length and coffee table length",
      head: ["Sofa", "Two-thirds", "Range to look for"],
      rows: [60, 72, 78, 84, 90, 96, 108].map((l) => {
        const t = coffeeTableSize(l);
        return [`${l} in.`, `${t.twoThirds} in.`, `${t.range[0]}–${t.range[1]} in.`];
      }),
    },
  ];
}

function stoolTables() {
  return [
    {
      caption: "Surface height and the stool it takes",
      head: ["Surface", "Height", "Seat height"],
      rows: [
        ["Table", "28–30 in.", "17–19 in. (a chair)"],
        ...STOOL_CLASSES.filter((c) => c.id !== "chair").map((c) => [
          { counter: "Kitchen counter or island", bar: "Raised bar or peninsula", spectator: "Extra-tall or commercial bar" }[c.id],
          c.surface[0] === c.surface[1] ? `${c.surface[0]} in.` : `${c.surface[0]}–${c.surface[1]} in.`,
          `${c.seat[0]}–${c.seat[1]} in. (${c.name})`,
        ]),
      ],
    },
    {
      caption: "How many stools fit along a counter",
      head: ["Counter run", `Backless (${STOOL_SPACING_IN.backless} in.)`, `With backs (${STOOL_SPACING_IN.back} in.)`, `With arms (${STOOL_SPACING_IN.arms} in.)`],
      rows: [48, 60, 72, 84, 96, 120].map((l) => [`${l} in.`, ...["backless", "back", "arms"].map((st) => String(stoolsThatFit(l, st)))]),
    },
  ];
}

function sofaDoorTables() {
  return [
    {
      caption: "Doorway width and the sofa that goes through flat",
      head: ["Clear door width", "Sofa depth or height up to", "With the door off its hinges"],
      rows: [28, 30, 32, 34, 36].map((w) => [
        `${w} in.`,
        `${w - PATH_CLEARANCE_IN} in.`,
        `${w - PATH_CLEARANCE_IN + DOOR_REMOVAL_GAIN_IN} in.`,
      ]),
    },
  ];
}

export const TOOLS = {
  "/tools/rug-size-calculator": {
    name: "Rug Size Calculator",
    summary: "The standard rug size for your living room, bed or dining table.",
    intro:
      "Enter your living room, bed or dining table and get the standard rug size that fits. The calculator uses the same rules as our rug size guide: leave 12 to 18 inches of bare floor at the walls, run the rug 18 to 24 inches past each side of a queen or king bed, and add 24 to 30 inches to every side of a dining table.",
    rules: [
      "Living rooms: keep the front legs of the sofa and chairs on the rug and leave 12 to 18 inches of bare floor at the walls (8 inches is fine in a small room). The rug should run 6 to 8 inches past each end of the sofa.",
      "Bedrooms: 9×12 for a king, 8×10 for a queen, 6×9 for a full and 5×8 for a twin. Start the rug about a third of the way down the bed, just in front of the nightstands.",
      "Dining tables: add 24 to 30 inches to every side of the table so pulled-out chairs stay on the rug, and match the rug's shape to the table's.",
      "Before you order, tape the outline on the floor and live with it for a day.",
    ],
    tables: rugTables,
    faq: [
      {
        q: "What size rug do I need for my living room?",
        a: "Most living rooms take an 8×10 (rooms around 11×14 ft) or a 9×12 (12×18 ft and larger). The calculator picks the largest standard size that still leaves 12 to 18 inches of bare floor at the walls.",
      },
      {
        q: "Should a rug be wider than the sofa?",
        a: "Yes. The rug should run 6 to 8 inches past each end of the sofa; a rug narrower than the sofa looks undersized however it's placed.",
      },
      {
        q: "What size rug goes under a queen bed?",
        a: "An 8×10, placed so 18 to 24 inches of rug show on each side and at the foot of the bed.",
      },
      {
        q: "How much bigger than the dining table should the rug be?",
        a: "24 to 30 inches on every side, so chairs stay on the rug when they're pulled out. A 36×60-inch table needs an 8×10; a 40×72-inch table is roomier on a 9×12.",
      },
    ],
    image: "/journal/01-hero.webp",
    guides: [
      { slug: "rug-size-guide", title: "What Size Rug Do You Need? The Complete Rug Size Guide for Every Room" },
      { slug: "outdoor-rug-size-guide", title: "Outdoor Rug Size Guide: Patio & Deck Sizing" },
      { slug: "area-rug-vs-runner-vs-layered-rugs", title: "Area Rug vs. Runner vs. Layered Rugs: A Placement Guide" },
      { slug: "dining-table-size-guide", title: "Dining Table Size Guide: How Many Seats, How Much Clearance, What Fits" },
    ],
    shop: [{ href: "/category/area-rugs", label: "Shop rugs" }],
  },
  "/tools/chandelier-size-calculator": {
    name: "Chandelier Size Calculator",
    summary: "Fixture diameter for your room or table, and how high to hang it.",
    intro:
      "Enter your room's size, your ceiling height and, for a dining room, your table's width. The calculator returns a fixture diameter and a hanging height using the rules in our chandelier size guide.",
    rules: [
      "Room: add the room's length and width in feet; that number, read in inches, is the fixture's diameter. When you're torn between two sizes, take the larger.",
      "Over a dining table: choose a fixture one-half to two-thirds of the table's width, and center it on the table rather than the room.",
      "Over a table, hang the lowest point 30 to 36 inches above the top with an 8-foot ceiling, and about 3 inches higher for each extra foot of ceiling.",
      "Anywhere people walk, keep at least 84 inches (7 feet) from the floor to the fixture's lowest point.",
    ],
    tables: chandelierTables,
    faq: [
      {
        q: "How do I calculate what size chandelier I need?",
        a: "Add the room's length and width in feet; the total, read in inches, is the target diameter. A 12×14 ft room suggests a fixture about 26 inches across. Over a dining table, use one-half to two-thirds of the table's width instead.",
      },
      {
        q: "How high should a chandelier hang over a dining table?",
        a: "With an 8-foot ceiling, the lowest point should sit 30 to 36 inches above the tabletop. Add about 3 inches for each foot of ceiling above 8 feet.",
      },
      {
        q: "How far can a light hang from an 8-foot ceiling?",
        a: "In a walking area, no more than 12 inches from ceiling to the fixture's lowest point, to keep 84 inches of clearance. Over a table nobody walks across, it can hang lower.",
      },
      {
        q: "What size chandelier goes over a 60-inch round table?",
        a: "One-half to two-thirds of the table's width: about 30 to 40 inches in diameter, hung 30 to 36 inches above the top.",
      },
    ],
    image: "/journal/10-hero.webp",
    guides: [
      { slug: "chandelier-size-guide", title: "Chandelier and Ceiling Light Size Guide: Getting Scale Right" },
      { slug: "pendant-light-height", title: "Pendant Lighting Height Over a Kitchen Island (Exact Numbers)" },
      { slug: "layered-lighting-101", title: "Layered Lighting 101: Ambient, Task, and Accent — Explained Simply" },
      { slug: "pendant-vs-flush-mount-vs-recessed-ceiling-lights", title: "Pendant vs. Flush Mount vs. Recessed: Choosing Ceiling Lights" },
    ],
    shop: [
      { href: "/category/chandeliers", label: "Shop chandeliers" },
      { href: "/category/pendant-lights", label: "Shop pendant lights" },
    ],
  },
  "/tools/dining-table-size-calculator": {
    name: "Dining Table Size Calculator",
    summary: "The table that seats your group and still leaves room to pull out chairs.",
    intro:
      "Enter your dining room's size and how many people you seat, and see the table that fits — with the clearance left on every side. The calculator uses the rules in our dining table size guide: 24 inches of table edge per person, at least 36 inches from the table to the wall, and 42 to 48 inches where people walk behind seated guests.",
    rules: [
      "Seats: allow 24 inches of table edge per person with standard chairs; armchairs need 28 to 30 inches, so subtract a seat or two if your chairs have arms.",
      "Clearance: keep at least 36 inches from the table's edge to any wall or piece of furniture — enough to pull out a chair and sit down.",
      "Walkways: allow 42 to 48 inches wherever someone walks behind a seated guest; the path to the kitchen earns the full 48.",
      "Largest table: subtract 72 inches from each room dimension. Treat that as a ceiling, not a target.",
    ],
    tables: diningTableTables,
    faq: [
      {
        q: "What size dining table seats six?",
        a: "A 60-inch rectangle or a 54-inch round seats six comfortably. A 72-inch rectangle seats six, or eight at a squeeze.",
      },
      {
        q: "How much space do you need around a dining table?",
        a: "At least 36 inches from the table's edge to the wall so a chair can be pulled out; 42 to 48 inches where people walk behind seated guests.",
      },
      {
        q: "What size dining table fits a 10×10 room?",
        a: "After 36 inches of clearance on every side, the largest footprint is about 48 × 48 inches, so a 42- to 48-inch round table seating four is the natural fit.",
      },
      {
        q: "How many people fit at a 72-inch table?",
        a: "Six comfortably, eight at a squeeze with armless chairs. For eight regular diners, an 84-inch table is the honest answer.",
      },
    ],
    image: "/journal/03-hero.webp",
    guides: [
      { slug: "dining-table-size-guide", title: "Dining Table Size Guide: How Many Seats, How Much Clearance, What Fits" },
      { slug: "round-vs-rectangular-dining-table", title: "Round vs. Rectangular Dining Table: How to Decide" },
      { slug: "best-dining-tables-for-small-spaces", title: "The Best Dining Tables for Small Spaces (2026 Buyer's Guide)" },
    ],
    shop: [{ href: "/category/dining-tables", label: "Shop dining tables" }],
  },
  "/tools/wall-art-size-calculator": {
    name: "Wall Art Size Calculator",
    summary: "How wide the art should be over a sofa, bed or console, and how high to hang it.",
    intro:
      "Enter the width of the sofa, bed or console below the art — or the stretch of open wall — and get the art width to look for and how high to hang it. The calculator uses the rules in our wall art size guide.",
    rules: [
      "Over furniture: the art, or the whole group, should span about two-thirds to three-quarters of the furniture's width.",
      "Height over furniture: leave 8 to 10 inches above a sofa back, 8 to 12 inches above a headboard and 6 to 10 inches above a console or dresser.",
      "Open wall: fill about 60 to 75 percent of the wall section, and center the art at 57 to 60 inches from the floor.",
      "Sets and gallery walls: measure the whole arrangement, frames plus the gaps between them, and hang it as one piece.",
    ],
    tables: wallArtTables,
    faq: [
      {
        q: "What size art goes over an 84-inch sofa?",
        a: "Art or a group about 56 to 63 inches wide — two-thirds to three-quarters of the sofa — with its bottom edge 8 to 10 inches above the sofa back.",
      },
      {
        q: "How high should art hang on an open wall?",
        a: "Center it at 57 to 60 inches from the floor, the eye-level height galleries use. For a group, center the whole arrangement at that height.",
      },
      {
        q: "How high above a bed should art hang?",
        a: "8 to 12 inches above the headboard, so the art relates to the bed rather than the ceiling.",
      },
      {
        q: "How far apart should frames in a set be?",
        a: "About 2 to 4 inches for a pair and 2 to 3 inches in a row or grid, kept consistent across the set.",
      },
    ],
    image: "/journal/147-hero.jpg",
    guides: [
      { slug: "wall-art-size-guide", title: "Wall Art Size Guide: Over a Sofa, Bed & More" },
      { slug: "how-to-plan-and-hang-a-gallery-wall", title: "How to Plan and Hang a Gallery Wall (with Real Spacing Numbers)" },
    ],
    shop: [
      { href: "/category/wall-decor", label: "Shop wall art" },
      { href: "/category/mirrors", label: "Shop mirrors" },
    ],
  },
  "/tools/lampshade-size-calculator": {
    name: "Lampshade Size Calculator",
    summary: "The shade width and height that suit your lamp base.",
    intro:
      "Measure your lamp's base and get the shade size to look for. The calculator uses the rules of thumb in our lampshade size guide: a shade about twice as wide as the base and about two-thirds as tall.",
    rules: [
      "Shade bottom: about twice the width of the widest part of the lamp base.",
      "Shade height: about two-thirds of the base height, measured from the bottom of the lamp to the socket.",
      "Coverage: the shade should hide the socket and harp from a normal standing or sitting position.",
      "On a table: the shade should be no wider than the table it sits on.",
      "Shade sizes are listed top × bottom × height (some makers give the slant instead of the height), and the fitter — spider, UNO or clip-on — has to match your lamp.",
    ],
    tables: lampshadeTables,
    faq: [
      {
        q: "How do I know what size lampshade to buy?",
        a: "Measure the widest part of the lamp base and its height from the bottom to the socket. Look for a shade about twice the base width at the bottom and about two-thirds of the base height.",
      },
      {
        q: "How do you measure a lampshade?",
        a: "Measure the top diameter, the bottom diameter and the height straight up the side. Listings give them as top × bottom × height — or slant, so check which.",
      },
      {
        q: "What if the new shade sits too high or too low?",
        a: "On a lamp with a spider fitter, the harp sets the height. A taller or shorter harp — they come in 1-inch steps — fixes a shade that shows the socket or the base's neck.",
      },
    ],
    image: "/journal/148-hero.jpg",
    guides: [
      { slug: "lampshade-size-guide", title: "Lampshade Size Guide: Measure, Fit & Style" },
      { slug: "table-lamp-size-guide", title: "Table Lamp Size Guide: How Tall & How Wide" },
      { slug: "floor-lamp-buying-guide", title: "Floor Lamp Buying Guide: Height, Light, and Placement" },
    ],
    shop: [
      { href: "/category/lampshades", label: "Shop lampshades" },
      { href: "/category/table-lamps", label: "Shop table lamps" },
    ],
  },
  "/tools/coffee-table-size-calculator": {
    name: "Coffee Table Size Calculator",
    summary: "The coffee table length, height and spacing that suit your sofa.",
    intro:
      "Enter your sofa's length, and its seat height if you know it, and get the coffee table size to look for, how high it should stand and where it goes. The calculator uses the rules in our coffee table guide: about two-thirds of the sofa's length, within an inch or two of the seat height, and 14 to 18 inches from the sofa.",
    rules: [
      "Length: about two-thirds of the sofa's length. Most people go a little shorter: for a standard 78 to 90 in. sofa, 44 to 54 inches reads relaxed.",
      "Height: level with the sofa seat or up to 2 inches below it. Standard seats run 16 to 20 inches, so most coffee tables do too.",
      `Distance: ${COFFEE_TABLE_GAP_IN[0]} to ${COFFEE_TABLE_GAP_IN[1]} inches between the front of the sofa and the table's edge.`,
      `Walkway: keep ${COFFEE_TABLE_WALKWAY_IN} inches clear on main paths around the table.`,
      "Depth: roughly half the table's length. A table no longer than the sofa keeps the seating from feeling crowded.",
    ],
    tables: coffeeTableTables,
    faq: [
      {
        q: "What size coffee table do I need for my sofa?",
        a: "About two-thirds of the sofa's length. A standard 78 to 90 inch sofa pairs well with a table around 44 to 54 inches; two-thirds exactly is 52 to 60.",
      },
      {
        q: "How tall should a coffee table be?",
        a: "Within one to two inches of your sofa's seat height, usually 16 to 20 inches. Level with the seat or slightly below is ideal.",
      },
      {
        q: "How far should a coffee table be from the couch?",
        a: "14 to 18 inches between the sofa edge and the table: close enough to reach, wide enough to walk past without turning sideways.",
      },
      {
        q: "Can a coffee table be bigger than the sofa?",
        a: "It generally shouldn't be longer than the sofa. Two-thirds of the sofa's length keeps the proportions calm.",
      },
    ],
    image: "/journal/44-hero.webp",
    guides: [
      { slug: "how-to-choose-a-coffee-table", title: "How to Choose a Coffee Table: Size, Shape, and Height" },
      { slug: "best-coffee-tables-for-small-living-rooms", title: "The Best Coffee Tables for Small Living Rooms (2026 Guide)" },
      { slug: "how-to-style-a-coffee-table", title: "Coffee Table Decor Ideas: The Tray-Stack-Greenery Method" },
    ],
    shop: [{ href: "/category/coffee-tables", label: "Shop coffee tables" }],
  },
  "/tools/bar-stool-height-calculator": {
    name: "Bar Stool Height Calculator",
    summary: "The stool seat height for your counter or bar, and how many stools fit.",
    intro:
      "Measure from the floor to the top of your counter, bar or table, and get the seat height to look for and how many stools fit along it. The calculator uses the rule in our bar and counter stool guide: leave 9 to 12 inches between the seat and the underside of the counter.",
    rules: [
      "Seat height: the surface height minus 9 to 12 inches, measured from the floor to the top of the finished countertop.",
      "A 36 in. kitchen counter takes a counter stool with a 24 to 26 in. seat; a 40 to 42 in. bar takes a 28 to 30 in. bar stool; a 44 to 47 in. surface takes a 33 to 36 in. spectator stool; a 28 to 30 in. table takes a chair.",
      "In between, such as a 38 in. island: choose an adjustable gas-lift stool.",
      `Spacing: ${STOOL_SPACING_IN.backless} inches per backless stool, ${STOOL_SPACING_IN.back} with backs, ${STOOL_SPACING_IN.arms} with arms. Each stool gets a zone that wide, so divide the counter's length by it.`,
      "Overhang: at least 6 inches of counter overhang for your knees, ideally 10 to 12 inches if you'll eat there.",
    ],
    tables: stoolTables,
    faq: [
      {
        q: "What height stool do I need for a 36-inch counter?",
        a: "A counter stool with a 24 to 26 inch seat. The 9 to 12 inch gap to the underside of the counter leaves room for your legs.",
      },
      {
        q: "What is the difference between counter and bar stools?",
        a: "Counter stools have a 24 to 26 inch seat for a 36 inch counter; bar stools have a 28 to 30 inch seat for a 40 to 42 inch bar. The names describe the seat height.",
      },
      {
        q: "How many stools fit at a 72-inch island?",
        a: "Three backless stools at 24 inches each, or two with arms at 30. Divide the counter's length by the spacing for your stool style.",
      },
      {
        q: "What if my counter is an odd height?",
        a: "Choose an adjustable gas-lift stool. It handles in-between heights such as 38 inches.",
      },
    ],
    image: "/journal/50-hero.webp",
    guides: [
      { slug: "bar-counter-stool-height-guide", title: "Bar and Counter Stool Height Guide: Counter vs. Bar, and How to Measure" },
      { slug: "home-bar-cart-styling", title: "The Home Bar Cart: How to Set Up and Style One That Works" },
    ],
    shop: [{ href: "/category/bar-chairs", label: "Shop bar and counter stools" }],
  },
  "/tools/sofa-door-fit-calculator": {
    name: "Sofa Door Fit Calculator",
    summary: "Will the sofa fit through your door? Check it flat or angled before you buy.",
    intro:
      "Measure your doorway's clear width with the door open, and enter the sofa's depth and height, plus its diagonal depth if the maker lists it. The calculator checks it the way our delivery measuring guide does, with 2 inches of clearance, and lists sofas in stock that would go through flat.",
    rules: [
      "Measure the doorway's clear width with the door open, at its narrowest point. Check every doorway, hallway turn, stairway and elevator on the route, not just the front door.",
      `Flat: if the sofa's depth or height, whichever is smaller, is at least ${PATH_CLEARANCE_IN} inches narrower than the opening, it goes through on its back or side.`,
      `Angled: otherwise compare its diagonal depth, from the top of the back frame down to the front of the arm, with the opening, again leaving ${PATH_CLEARANCE_IN} inches.`,
      `Taking the door off its hinges adds about ${DOOR_REMOVAL_GAIN_IN} inches of width.`,
      "Sectionals arrive in pieces: check the largest piece.",
    ],
    tables: sofaDoorTables,
    faq: [
      {
        q: "How do I know if my sofa will fit through the door?",
        a: "Compare the sofa's depth or height, whichever is smaller, with the doorway's clear width, leaving 2 inches. If both are too big, compare its diagonal depth: sofas are angled through doors.",
      },
      {
        q: "How do I measure a sofa's diagonal depth?",
        a: "Measure from the highest point of the back frame diagonally down to the front of the arm or seat. If the product page doesn't list it, ask before you buy.",
      },
      {
        q: "What if the sofa won't fit through the door?",
        a: "Look for a sofa that ships in sections or has removable legs, take the door off its hinges for about 2 more inches, or try another entrance.",
      },
    ],
    image: "/journal/93-hero-v2.webp",
    guides: [
      { slug: "how-to-measure-for-furniture-delivery", title: "How to Measure for Furniture Delivery (Doorways, Stairs, Elevators)" },
      { slug: "sofa-dimensions-guide", title: "Sofa Dimensions 101: How to Measure Your Space Before You Buy" },
      { slug: "sofa-buying-mistakes", title: "7 Sofa-Buying Mistakes Designers See All the Time (and How to Avoid Every One)" },
    ],
    shop: [
      { href: "/category/sofas", label: "Shop sofas" },
      { href: "/category/love-seats", label: "Shop loveseats" },
    ],
  },
  "/tools/christmas-tree-size-calculator": {
    name: "Christmas Tree Size Calculator",
    summary: "The tallest tree for your ceiling, how wide it will be, and how many lights and ornaments to plan.",
    intro:
      "Enter your ceiling height, the shape of tree you want and, if you like, the space you have for it. The calculator uses the rules in our Christmas tree size guide: about a foot less than the ceiling, a full tree about two-thirds as wide as it is tall, and a clear path around it.",
    rules: [
      `Height: your ceiling height less about ${TREE_HEADROOM_IN} inches, which covers the stand and a topper. An 8-foot ceiling takes a 7-foot tree.`,
      "Width: full trees run about 55 to 75 percent as wide as they are tall; slim trees about half; pencil trees narrower still. Listings give the diameter at the widest point, near the bottom.",
      `Space: leave at least ${TREE_PATH_IN} inches clear on any path people walk through, plus room in front of the tree for presents.`,
      `Lights: about ${TREE_LIGHTS_PER_FT} per foot of height for a classic look.`,
      `Ornaments: ${TREE_ORNAMENTS_PER_FT[0]} to ${TREE_ORNAMENTS_PER_FT[1]} per foot of height, more if they're small.`,
      "Check whether the listed height includes the stand. If the stand is sold separately or the topper is tall, leave a little more.",
    ],
    tables: christmasTreeTables,
    faq: [
      {
        q: "What size Christmas tree for an 8-foot ceiling?",
        a: "A 7-foot tree. Subtract about 12 inches from the ceiling height to leave room for the stand and a topper.",
      },
      {
        q: "How wide is a 7-foot Christmas tree?",
        a: "A full 7-foot tree is usually about 4 to 5 feet across at the bottom; a slim one is closer to 3½ feet. Check the listed diameter and tape it out on the floor.",
      },
      {
        q: "How many lights do I need for a 7-foot tree?",
        a: "About 700, using the rule of thumb of 100 lights per foot of height. Use more for a dense glow, fewer for a softer look.",
      },
    ],
    image: "/journal/154-hero.webp",
    guides: [
      { slug: "christmas-tree-size-guide", title: "What Size Christmas Tree Do I Need? Height, Width, Lights & Ornaments" },
      { slug: "christmas-tree-decorating-ideas", title: "Christmas Tree Decorating Ideas: The Order That Always Works" },
      { slug: "how-to-store-christmas-ornaments", title: "How to Store Christmas Ornaments So They Survive Every Year" },
    ],
    shop: [
      { href: "/category/christmas-trees", label: "Shop Christmas trees" },
      { href: "/category/ornaments", label: "Shop ornaments" },
    ],
  },
};

export const TOOL_PATHS = Object.keys(TOOLS);

/** The /tools hub that lists every calculator. */
export const TOOLS_HUB = {
  path: "/tools",
  name: "Sizing Tools",
  intro:
    "Free calculators for the measurements that decide whether a piece fits: rug size, chandelier size, dining table size, wall art size, lampshade size, coffee table size, bar stool height, whether a sofa will get through your door, and the right Christmas tree for your ceiling. Each one uses the rules from our sizing guides, and shows its working.",
};

/** BreadcrumbList + ItemList for the hub, emitted by the prerender and React. */
export function toolsHubSchemas() {
  const url = `${SITE}${TOOLS_HUB.path}`;
  return [
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE },
        { "@type": "ListItem", position: 2, name: TOOLS_HUB.name, item: url },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      itemListElement: TOOL_PATHS.map((p, i) => ({ "@type": "ListItem", position: i + 1, name: TOOLS[p].name, url: `${SITE}${p}` })),
    },
  ];
}

/** BreadcrumbList + FAQPage, emitted by the prerender and by the React page. */
export function toolSchemas(path) {
  const tool = TOOLS[path];
  const url = `${SITE}${path}`;
  return [
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE },
        { "@type": "ListItem", position: 2, name: TOOLS_HUB.name, item: `${SITE}${TOOLS_HUB.path}` },
        { "@type": "ListItem", position: 3, name: tool.name, item: url },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: tool.faq.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ];
}
